import React, { useState, useCallback } from "react";
import {
	Modal as ChakraModal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Switch,
	Text,
} from "@chakra-ui/react";
import { Button } from "newui/components/Button";

interface SavePresetDialogProps {
	/** When editing, pre-fill with existing preset data */
	initialValues?: {
		description: string;
		includeAdditionalText: boolean;
		includeCoverPage: boolean;
		name: string;
	};
	isOpen: boolean;
	isSaving: boolean;
	mode: "create" | "update";
	onClose: () => void;
	onSave: (data: {
		description: string;
		includeAdditionalText: boolean;
		includeCoverPage: boolean;
		name: string;
	}) => void;
}

const SavePresetDialog: React.FC<SavePresetDialogProps> = ({
	isOpen,
	onClose,
	onSave,
	isSaving,
	initialValues,
	mode,
}) => {
	const [name, setName] = useState(initialValues?.name ?? "");
	const [description, setDescription] = useState(initialValues?.description ?? "");
	const [includeCoverPage, setIncludeCoverPage] = useState(initialValues?.includeCoverPage ?? false);
	const [includeAdditionalText, setIncludeAdditionalText] = useState(
		initialValues?.includeAdditionalText ?? false
	);

	// Reset form when dialog opens with new initial values
	React.useEffect(() => {
		if (isOpen) {
			setName(initialValues?.name ?? "");
			setDescription(initialValues?.description ?? "");
			setIncludeCoverPage(initialValues?.includeCoverPage ?? false);
			setIncludeAdditionalText(initialValues?.includeAdditionalText ?? false);
		}
	}, [isOpen, initialValues]);

	const handleSubmit = useCallback(() => {
		if (!name.trim()) return;
		onSave({
			name: name.trim(),
			description: description.trim(),
			includeCoverPage,
			includeAdditionalText,
		});
	}, [name, description, includeCoverPage, includeAdditionalText, onSave]);

	const isCreate = mode === "create";

	return (
		<ChakraModal isOpen={isOpen} onClose={onClose} size="md" isCentered>
			<ModalOverlay bg="blackAlpha.400" />
			<ModalContent borderRadius="lg">
				<ModalCloseButton />
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<Text fontSize="lg" fontWeight="bold">
						{isCreate ? "Save New Preset" : "Update Preset"}
					</Text>
				</ModalHeader>

				<ModalBody py={5}>
					<div className="flex flex-col gap-4">
						{/* Name */}
						<div>
							<label className="mb-1 block text-sm font-medium text-gray-700">
								Preset Name <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. Standard Report, Minimal View"
								maxLength={255}
								className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
						</div>

						{/* Description */}
						<div>
							<label className="mb-1 block text-sm font-medium text-gray-700">
								Description
							</label>
							<textarea
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Optional description of this preset"
								maxLength={1000}
								rows={2}
								className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
							<span className="mt-0.5 block text-right text-xs text-gray-400">
								{description.length}/1000
							</span>
						</div>

						{/* Include toggles */}
						<div className="rounded-md border border-gray-100 bg-gray-50 p-3">
							<p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
								Include in preset
							</p>
							<div className="flex flex-col gap-2">
								<div className="flex items-center justify-between">
									<span className="text-sm text-gray-700">Cover page settings</span>
									<Switch
										isChecked={includeCoverPage}
										onChange={(e) => setIncludeCoverPage(e.target.checked)}
										colorScheme="blue"
										size="sm"
									/>
								</div>
								<div className="flex items-center justify-between">
									<span className="text-sm text-gray-700">Additional text page</span>
									<Switch
										isChecked={includeAdditionalText}
										onChange={(e) => setIncludeAdditionalText(e.target.checked)}
										colorScheme="blue"
										size="sm"
									/>
								</div>
							</div>
						</div>
					</div>
				</ModalBody>

				{/* Footer */}
				<div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
					<Button btntype="secondary" size="sm" onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button
						btntype="primary"
						size="sm"
						onClick={handleSubmit}
						isLoading={isSaving}
						loadingText="Saving..."
						disabled={!name.trim()}
					>
						{isCreate ? "Save Preset" : "Update Preset"}
					</Button>
				</div>
			</ModalContent>
		</ChakraModal>
	);
};

export default SavePresetDialog;
